import {Permission, Role} from './enums&types.js';

// the role -> permission table that used to be the createRole calls in initStaticObjects.ts
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
    [Role.admin]           : [Permission.admin, Permission.dashboard],
    [Role.reporter]        : [Permission.report, Permission.dashboard],
    [Role.super_reporter]  : [Permission.api],
    [Role.acceptant]       : [Permission.accept],
    [Role.exporter]        : [Permission.export],
    [Role.reporter_manager]: [Permission.manage_reporters],
    [Role.dashboard_viewer]: [Permission.dashboard],
};

// the roles whose access follows the user's instelling/thema combinations (Auth.ts filters, dashboard scope); other roles have no use for them
export const ROLLEN_MET_INSTELLING: Role[] = [Role.reporter, Role.acceptant, Role.reporter_manager];

export function permissiesVan(roles: Role[]): Permission[] {
    return [...new Set(roles.flatMap(r => ROLE_PERMISSIONS[r] ?? []))];
}

// where a login lands, first match wins; a user with none of these permissions has no page to land on
export const LANDINGEN: { permissie: Permission, pad: string }[] = [
    {permissie: Permission.admin,            pad: '/admin'},
    {permissie: Permission.report,           pad: '/report'},
    {permissie: Permission.accept,           pad: '/accept'},
    {permissie: Permission.manage_reporters, pad: '/report_manager'},
    {permissie: Permission.dashboard,        pad: '/dashboard'},
];

export function landingVan(permissies: string[]): string | undefined {
    return LANDINGEN.find(l => permissies.includes(l.permissie))?.pad;
}

export const SESSIE_IDLE_DAGEN: number           = 2;
export const SESSIE_MAX_DAGEN: number            = 7;
export const RESET_TOKEN_UREN: number            = 1;
export const ONBOARDING_TOKEN_DAGEN: number      = 7;
export const LOGIN_CODE_MINUTEN: number          = 10;
export const LOGIN_CODE_MAX_POGINGEN: number     = 5;
export const VERTROUWDE_BROWSER_DAGEN: number    = 30;
export const WACHTWOORD_MIN_LENGTE: number       = 8;
export const THROTTLE_MAX_POGINGEN: number       = 5;
export const THROTTLE_VENSTER_MINUTEN: number    = 5;
export const THROTTLE_BLOKKADE_SECONDEN: number  = 30;
export const SESSIE_COOKIE: string               = 'zzc_sessie';
export const BROWSER_COOKIE: string              = 'zzc_browser';

export type TokenDoel = 'reset' | 'onboarding' | 'login';
export type MailSoort = 'welkom' | 'reset' | 'code';

// what the AuthLog records: the three mails, and what happens with a reset or onboarding link afterwards
export type AuthLogSoort = MailSoort | 'link-geopend' | 'link-ongeldig' | 'wachtwoord-gezet' | 'impersonatie-start' | 'impersonatie-stop';

export const AUTH_LOG_LABELS: Record<AuthLogSoort, string> = {
    'welkom'            : 'welkomstmail',
    'reset'             : 'wachtwoordmail',
    'code'              : 'inlogcode',
    'link-geopend'      : 'link geopend',
    'link-ongeldig'     : 'ongeldige link',
    'wachtwoord-gezet'  : 'wachtwoord gezet',
    'impersonatie-start': 'bekijken als',
    'impersonatie-stop' : 'bekijken gestopt',
};

// one AuthLog node; answer of GET /api/admin/auth-log is a list of these, newest first.
// A mail's tekst (with its link or code) is only kept where no mail is sent, so a production log never holds a live
// secret; an invalid link is logged with the ip only, never with the token that was tried
export type AuthLogRegel = {
    id: string;
    tijd: string; // ISO
    soort: AuthLogSoort;
    aan?: string;    // the user's e-mail; absent for an invalid link
    door?: string;   // the admin, for the impersonation kinds
    ip?: string;     // the link kinds
    gelukt: boolean; // mail sent, link valid, password set
    fout?: string;   // the SMTP error when sending failed
    tekst?: string;  // the mail text, only when MAIL_VERSTUREN is off
};

// the 409 answer to a write while an admin is looking as another user
export const IMPERSONATIE_MELDING: string = 'alleen bekijken tijdens impersoneren';

// where a user stands with the welcome mail; answer of GET /api/obj/user/welkom-status, one per lowercased e-mail:
// kandidaat = the button mails this user now; incompleet = no password yet but no role, or a reporter without
// instelling/thema, or no valid e-mail; open-link = mailed, link still open; wachtwoord = can log in
export type WelkomStatus = 'kandidaat' | 'incompleet' | 'open-link' | 'wachtwoord';

// one live session of a user; answer of POST /api/obj/user/sessies. id is the stored hash, never the secret
export type SessieInfo = {
    id: string;
    aangemaaktOp: string; // ISO
    laatstGezienOp: string;
    ip: string;
    browser: string;
    huidig: boolean; // the session the caller is using now
};

// live sessions and trusted browsers of one user; answer of POST /api/obj/user/sessie-overzicht is one per e-mail
export type SessieTelling = {
    sessies: number;
    browsers: number;
};

// answer of POST /api/auth/start: log in with a password, a set-password mail has just been sent, a login code has just
// been sent (staff), or staff outside the office network
export type StartAntwoord = {
    status: 'wachtwoord' | 'mail' | 'code' | 'kantoor' | 'geblokkeerd';
};

// answer of POST /api/auth/login and /api/auth/login-code
export type LoginAntwoord = {
    status: 'ok' | 'code' | 'fout' | 'geblokkeerd';
    pogingenOver?: number; // login-code: attempts left after a wrong code
};

// answer of POST /api/auth/wachtwoord-zetten
export type WachtwoordAntwoord = {
    status: string;
    message?: string;
};
